import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
    Box,
    Paper,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    Fab,
    Tooltip,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";

import { getDashboardConfig } from "../../config";
import { SHOW_VARIANT_SWITCHER, getVariants } from "../../variants/registry";
import { useVariant } from "../../variants/useVariant";

/**
 * Panel flotante para cambiar en vivo la variante de diseño de la vista actual.
 * Herramienta de revision (no forma parte del diseño final): se apaga con
 * SHOW_VARIANT_SWITCHER = false. Solo aparece si la vista tiene 2+ variantes.
 */
function VariantSwitcher() {
    const { t } = useTranslation();
    const location = useLocation();
    const [open, setOpen] = useState(true);

    const { views: dashboardViews } = getDashboardConfig();
    const view = dashboardViews.find((v) => v.path === location.pathname);
    const viewKey = view?.key;
    const variants = getVariants(viewKey);
    const { variantId, setVariant } = useVariant(viewKey);

    if (!SHOW_VARIANT_SWITCHER || variants.length < 2) return null;

    if (!open) {
        return (
            <Tooltip title={t("variants.title")} placement="left">
                <Fab
                    size="medium"
                    onClick={() => setOpen(true)}
                    sx={{ position: "fixed", bottom: 24, right: 24, bgcolor: "navy.main", color: "#fff", "&:hover": { bgcolor: "navy.dark" } }}
                >
                    <TuneRoundedIcon />
                </Fab>
            </Tooltip>
        );
    }

    return (
        <Paper
            elevation={6}
            sx={{
                position: "fixed",
                bottom: 24,
                right: 24,
                width: 264,
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                zIndex: (theme) => theme.zIndex.tooltip,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TuneRoundedIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" color="text.primary">
                        {t("variants.title")}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={() => setOpen(false)} aria-label={t("variants.hide")}>
                    <CloseRoundedIcon fontSize="small" />
                </IconButton>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                {t("variants.hint")}
            </Typography>

            <ToggleButtonGroup
                orientation="vertical"
                exclusive
                fullWidth
                value={variantId}
                onChange={(_, id) => id && setVariant(id)}
                size="small"
            >
                {variants.map((v) => (
                    <ToggleButton
                        key={v.id}
                        value={v.id}
                        sx={{ justifyContent: "flex-start", textTransform: "none", py: 0.75 }}
                    >
                        {t(v.labelKey)}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Paper>
    );
}

export default VariantSwitcher;
